import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Thumbnail,
  DropZone,
  InlineError,
  InlineStack,
  Button,
  Loading,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { ImagesIcon } from "@shopify/polaris-icons";
import Select2 from "react-select";
import * as AiIcons from "react-icons/ai";
const AddEditBenchmark = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    name: "",
    image: "",
    country: "",
    channel: "",
    category: "",
    subcategory: "",
    icon: "",
  });

  const [errors, setErrors] = useState({});
  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  // Toast
  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  // Delete modal
  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);
  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/benchmark-parents/${id}`)
      .then(() => navigate(`/admin/benchmarks`))
      .catch(console.error);
  };

  // Image upload
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [files, setFiles] = useState();
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const imageKey = useRef("");
  const [imageError, setImageError] = useState("");

  const handleDropZone = useCallback((_dropFiles, acceptedFiles) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "benchmark_parent");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem((prev) => ({
          ...prev,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        }));
        setImageError("");
      })
      .catch(() => {});
  };

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );

  // Static categories + subcategories
  // const categories = [
  //   { label: "Category1", value: "category1" },
  //   { label: "Category2", value: "category2" },
  //   { label: "Category3", value: "category3" },
  // ];

  // const subcategoriesMap = {
  //   category1: [
  //     { label: "Subcategory1", value: "subcategory1" },
  //     { label: "Subcategory2", value: "subcategory3" },
  //   ],
  //   category2: [
  //     { label: "Subcategory1", value: "subcategory1" },
  //     { label: "Subcategory2", value: "subcategory3" },
  //   ],
  //   category3: [
  //     { label: "Subcategory1", value: "subcategory1" },
  //     { label: "Subcategory2", value: "subcategory3" },
  //   ],
  // };

  // const subcategories = subcategoriesMap[item.category] || [];

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  useEffect(() => {
    axios
      .get("/benchmarks/categories") // Replace with your real API endpoint
      .then((res) => {
        const categoriesData = res.data.data.data || [];
        console.log("categoriesData=", categoriesData);
        setCategories(
          categoriesData.map((cat) => ({
            label: cat.name,
            value: String(cat.id),
          }))
        );
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!item.category) {
      setSubcategories([]);
      return;
    }

    console.log("Fetching subcategories for:", item.category); // ✅ check this
    axios
      .get(`/benchmarks/categories/${item.category}/subcategories`)
      .then((res) => {
        const subcats = res.data.data.data || [];
        setSubcategories(
          subcats.map((s) => ({ label: s.name, value: String(s.id) }))
        );
      })
      .catch(console.error);
  }, [item.category]);

  // Validation
  const validateFields = () => {
    // Required fields — you can expand later if you add multi-language ones
    const requiredFields = {
      name: null,
      country: null,
      channel: null,
      // category: null,
      // subcategory: null,
    };

    const newErrors = {};
    let hasError = false;

    for (const field in requiredFields) {
      const langs = requiredFields[field];
      if (langs) {
        // If field requires per-language validation
        newErrors[field] = {};
        langs.forEach((lang) => {
          if (!item[field]?.[lang]?.toString().trim()) {
            hasError = true;
            newErrors[field][lang] = "This field is required";
          } else {
            newErrors[field][lang] = "";
          }
        });
      } else {
        // Normal single-value fields
        if (
          item[field] === "" ||
          item[field] === null ||
          item[field] === undefined
        ) {
          hasError = true;
          newErrors[field] = "This field is required";
        } else {
          newErrors[field] = "";
        }
      }
    }

    // Special case for image
    // if (!item.image) {
    //   hasError = true;
    //   setImageError("Image is required");
    // } else {
    //   setImageError("");
    // }

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));

    return !hasError;
  };

  // Save
  function handleSave() {
    if (!validateFields()) return;
    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");

    formData.append("name", item.name);
    formData.append("country", item.country);
    formData.append("channel", item.channel);
    formData.append("category_id", item.subcategory);
    item.icon && formData.append("icon", item.icon);
    // formData.append("subcategory", item.subcategory);
    imageKey.current && formData.append("image", imageKey.current);

    axios
      .post(
        mode === "new" ? "/benchmark-parents" : `/benchmark-parents/${id}`,
        formData
      )
      .then(() => {
        mode === "new" ? navigate("/admin/benchmarks") : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
      })
      .finally(() => setIsSaving(false));
  }
  useEffect(() => {
    if (mode === "edit") {
      axios
        .get(`/benchmark-parents/${id}`)
        .then((result) => {
          const data = result?.data?.data || {};

          setItem({
            name: data?.name || "",
            image: data?.image_url || "",
            country: data?.country || "",
            channel: data?.channel || "",
            category: String(data?.sub_category?.category?.id) || "",
            subcategory: String(data?.sub_category?.id) || "",
            icon: data?.icon || "",
          });

          console.log("okk");
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, [mode, id]);
  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);

  const useIconOptions = (iconPack) => {
    return useMemo(() => {
      return Object.keys(iconPack).map((key) => ({
        value: key,
        label: key,
        icon: iconPack[key],
      }));
    }, [iconPack]);
  };
  const options = useIconOptions(AiIcons);
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Benchmark`}
      backAction={{ onAction: () => navigate(`/admin/benchmarks`) }}
    >
      {isSaving && <Loading />}
      <Card sectioned>
        <FormLayout>
          <TextField
            label="Name"
            value={item.name}
            onChange={(value) => handleChange("name", value)}
            error={errors.name}
          />

          <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("country", value)}
            value={item.country}
            placeholder="Please select"
            error={errors.country}
          />

          <Select
            label="Channel"
            options={[
              { label: "Mobile", value: "Mobile" },
              { label: "Desktop", value: "Desktop" },
              { label: "Mobile Web", value: "Mobile Web" },
            ]}
            onChange={(value) => handleChange("channel", value)}
            value={item.channel}
            placeholder="Please select"
            error={errors.channel}
          />
          <Select
            label="Category"
            options={categories}
            value={item.category}
            onChange={(value) => {
              setItem((prev) => ({
                ...prev,
                category: value,
                subcategory: "", // reset together
              }));
              setErrors((prev) => ({ ...prev, category: "" }));
            }}
            placeholder="Select a category"
          />

          <Select
            label="Subcategory"
            options={subcategories}
            value={item.subcategory}
            onChange={(value) => handleChange("subcategory", value)}
            placeholder="Select a subcategory"
          />
          <Text>Icon</Text>
          <Select2
            options={options}
            getOptionLabel={(option) => (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {option.icon && <option.icon size={18} />}
                {option.label}
              </div>
            )}
            onChange={(selected) => {
              setItem((prev) => ({ ...prev, icon: selected?.value || "" }));
            }}
            // onChange={(selected) => {
            //   console.log("Selected option:", selected);
            //   console.log("Value:", selected?.value);
            // }}
            getOptionValue={(option) => option.value}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({ ...base, zIndex: 999999 }),
            }}
            menuPortalTarget={document.body}
          />
          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.image ? item.image : ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          { content: "No", onAction: toggleDeleteModal, loading: isSaving },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );
};

export default AddEditBenchmark;
